import { CommonMainPage } from '../../widgets/utility/CommonMainPage'
import { BpTooltip } from '../../shared/components/cards/tooltip/BpTooltip'
import { BpTabs } from '../../shared/components/utility/tabs/BpTabs'
import { BpTab } from '../../shared/components/utility/tabs/tab/BpTab'
import { ByReferralTable } from './widgets/by-referral-table/ByReferralTable'
import { ByMonthTable } from './widgets/by-month-table/ByMonthTable'
import { HowItWorks } from './widgets/how-it-works/HowItWorks'
import { hideModal, setModal, showAlert } from '../../app/store/reducers/AppSlice'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../app/store/hooks/Redux'
import { BpSelect } from '../../shared/components/forms/select/BpSelect'
import { IconFacebook } from '../../shared/components/icons/IconFacebook'
import { IconLinkedin } from '../../shared/components/icons/IconLinkedin'
import { IconTelegram } from '../../shared/components/icons/IconTelegram'
import { IconWhatsapp } from '../../shared/components/icons/IconWhatsapp'
import { SHARE_LINKS } from '../../shared/consts/share-links.const'
import { getDevice } from '../../shared/lib/helpers/get-device'
import {
  useGetReferralBalanceQuery,
  useGetReferralConversionStatQuery,
  useGetReferralLinkListQuery,
  useGetReferralPayoutsListQuery,
  useGetReferralTermsQuery,
  usePostReferralPayoutRequestMutation,
  usePostUserRequestChangeReferralTermsMutation,
} from '../../app/api'
import React, { useEffect, useMemo } from 'react'
import {
  selectReferrerProgramCurrency,
  selectReferrerProgramYear,
  setCurrency,
  setYear,
} from './model/referralProgramSlice'
import { PayoutsList } from './widgets/payouts-list/PayoutsList'
import { ConfirmContactModal } from '../../shared/components/utility/confirm-contact-modal/ConfirmContactModal'
import { IconMoneyWithdrawal } from '../../shared/components/icons/IconMoneyWithdrawal'
import { BpButton } from '../../shared/components/buttons/bp-button/BpButton'
import { setCurrencySymbol } from '../../shared/lib/helpers/set-currency-symbol'
import { ContentLoader } from '../../lib/ui/loader/components/content-loader/ContentLoader'
import { selectUser } from '../../app/store/reducers/AuthSlice'

export const ReferralProgramPage = () => {
  const { t } = useTranslation()
  const user = useAppSelector(selectUser)
  const year = useAppSelector(selectReferrerProgramYear)
  const currency = useAppSelector(selectReferrerProgramCurrency)
  const dispatch = useAppDispatch()

  const {
    data: balance,
    isLoading: balanceIsLoading,
    refetch: balanceRefetch,
  } = useGetReferralBalanceQuery()
  const { refetch: payoutsListRefetch } = useGetReferralPayoutsListQuery()
  const { data: conversionStat, isLoading: conversionStatIsLoading } =
    useGetReferralConversionStatQuery({})
  const { data: linkList, isLoading: linkListIsLoading } = useGetReferralLinkListQuery()
  const { data: terms, isLoading: termsIsLoading } = useGetReferralTermsQuery()
  const conversion = useMemo(
    () => ({
      clicks: conversionStat?.data?.clicks ?? 0,
      signups: conversionStat?.data?.signups ?? 0,
      paid_referrals: conversionStat?.data?.paid_referrals ?? 0,
    }),
    [conversionStat],
  )

  const showConversionRate = useMemo(
    () => !!(conversion.clicks + conversion.signups + conversion.paid_referrals),
    [conversion],
  )

  const [payoutRequest, { isLoading: payoutRequestIsFetching, isSuccess: payoutRequestIsSuccess }] =
    usePostReferralPayoutRequestMutation()

  const [
    changeReferralTerms,
    { isLoading: changeReferralTermsIsFetching, isSuccess: changeReferralTermsIsSuccess },
  ] = usePostUserRequestChangeReferralTermsMutation()

  const statBar = useMemo(() => {
    const max = Math.max(...Object.values(conversion))

    const toPercent = (value: number) => (max === 0 ? 0 : Number(((value / max) * 100).toFixed(1)))

    return {
      clicks: toPercent(conversion.clicks),
      signups: toPercent(conversion.signups),
      paid_referrals: toPercent(conversion.paid_referrals),
    }
  }, [conversion])

  const hasStat = useMemo(
    () => !!conversion.clicks || !!conversion.signups || !!conversion.paid_referrals,
    [conversion],
  )

  const referralCode = useMemo(() => {
    if (!linkList?.data?.length) return undefined

    const sorted = [...linkList.data].sort((a, b) => a.id - b.id)
    const lastItem = sorted[sorted.length - 1]

    return lastItem?.code
  }, [linkList])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${t('project.application')}/r/${referralCode}`)

      dispatch(
        showAlert({
          message: t('pages.referralProgram.successfullyCopied'),
          timeout: 2000,
          type: 'success',
        }),
      )
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Error',
          timeout: 2000,
          type: 'error',
        }),
      )
    }
  }

  const handleShare = (type: 'facebook' | 'linkedin' | 'telegram' | 'whatsapp') => {
    const device = getDevice()
    const url = `${t('project.application')}/r/${referralCode}`
    const message = encodeURIComponent(t('pages.referralProgram.shareText', { url }))

    if (device)
      switch (type) {
        case 'facebook':
          window.open(SHARE_LINKS.FACEBOOK[device](message, url), '_blank')
          break
        case 'linkedin':
          window.open(SHARE_LINKS.LINKEDIN[device](message, url), '_blank')
          break
        case 'telegram':
          window.open(SHARE_LINKS.TELEGRAM[device](message), '_blank')
          break
        case 'whatsapp':
          window.open(SHARE_LINKS.WHATSAPP[device](message), '_blank')
          break
      }
  }

  const submitPayoutRequest = () => {
    payoutRequest()
      .unwrap()
      .then((response) => {
        if (!response.success) return

        dispatch(
          showAlert({
            message: t('notifications.requestSentSuccessfully'),
            timeout: 2000,
            type: 'success',
          }),
        )
        balanceRefetch()
        payoutsListRefetch()
      })
      .catch(() => undefined)
  }

  const submitChangeReferralTerms = () => {
    changeReferralTerms()
      .unwrap()
      .then((response) => {
        if (!response.success) return

        dispatch(
          showAlert({
            message: t('notifications.requestSentSuccessfully'),
            timeout: 2000,
            type: 'success',
          }),
        )
      })
      .catch(() => undefined)
  }

  const handleContactConfirmed = (callback: () => void) => {
    if (user?.is_contact_confirmed) {
      callback()
    } else {
      dispatch(
        setModal({
          size: 'md',
          children: (
            <ConfirmContactModal
              onSuccess={() => {
                dispatch(hideModal())
                callback()
              }}
            />
          ),
        }),
      )
    }
  }

  useEffect(() => {
    if (terms?.data) dispatch(setCurrency(terms.data.currencySymbol))
  }, [terms])

  const PeriodSelect = () => {
    return (
      <BpSelect
        options={[
          { text: '2026', value: '2026' },
          { text: '2025', value: '2025' },
          { text: '2024', value: '2024' },
        ]}
        className={'-mb-[8px] w-[120px] lg:mb-0'}
        defaultValue={year}
        fieldSize={'sm'}
        onChange={(e) => dispatch(setYear(e.target.value))}
      />
    )
  }

  return (
    <CommonMainPage title={t('navigation.referralProgram')}>
      <div
        className={`${balance?.data?.available ? 'flex' : 'hidden'} mb-[16px] justify-between rounded-[12px] bg-[#FDFF99] px-[16px] py-[12px]`}
      >
        <div className={'flex items-center gap-[12px]'}>
          <div className={'w-[32px]'}>
            <IconMoneyWithdrawal color={'#00326D'} />
          </div>
          <div>
            <p className={'text-[20px] font-medium leading-[24px] text-focus-500'}>
              {t('pages.referralProgram.withdrawTitle', { amount: balance?.data?.available })}
            </p>
            <p className={'text-[16px] leading-[24px] text-focus-500'}>
              {t('pages.referralProgram.withdrawSubTitle')}
            </p>
          </div>
        </div>
        <div className={'flex items-center'}>
          <BpButton
            className={'uppercase'}
            onClick={() => {
              handleContactConfirmed(submitPayoutRequest)
            }}
          >
            {t('pages.referralProgram.buttons.withdrawEarnings')}
          </BpButton>
        </div>
      </div>
      <div className={'flex flex-col gap-[16px] lg:flex-row'}>
        <div className={'order-2 flex w-full flex-col gap-[18px] lg:order-1'}>
          <div className={'flex flex-col justify-between gap-[16px] md:flex-row'}>
            <div
              className={
                'flex w-full flex-col gap-[12px] rounded-[12px] bg-focus/[0.05] px-[16px] py-[12px]'
              }
            >
              <ContentLoader loading={balanceIsLoading}>
                <div className={'flex items-center gap-[6px]'}>
                  <p className={'text-[16px] font-semibold leading-[24px] text-aqua-300'}>
                    {t('pages.referralProgram.totalEarned')}
                  </p>
                  <BpTooltip title={t('pages.referralProgram.tooltips.totalEarned')} />
                </div>
                <div className={'text-[24px] font-medium leading-[32px] text-focus-600'}>
                  {setCurrencySymbol(currency, balance?.data?.total_earnings ?? 0)}
                </div>
              </ContentLoader>
            </div>
            <div
              className={
                'flex w-full flex-col gap-[12px] rounded-[12px] bg-focus/[0.05] px-[16px] py-[12px]'
              }
            >
              <ContentLoader loading={balanceIsLoading}>
                <div className={'flex items-center gap-[6px]'}>
                  <p className={'text-[16px] font-semibold leading-[24px] text-aqua-300'}>
                    {t('pages.referralProgram.balance')}
                  </p>
                  <BpTooltip title={t('pages.referralProgram.tooltips.pending')} />
                </div>
                <div className={'flex items-end gap-[8px]'}>
                  <p className={'text-[24px] font-medium leading-[32px] text-focus-600'}>
                    {setCurrencySymbol(currency, balance?.data?.available ?? 0)}
                  </p>
                  <span className={'text-[14px] text-aqua-300'}>
                    {t('pages.referralProgram.pending')}:{' '}
                    {setCurrencySymbol(currency, balance?.data?.pending ?? 0)}
                  </span>
                </div>
              </ContentLoader>
            </div>
            <div
              className={
                'flex w-full flex-col gap-[12px] rounded-[12px] bg-focus/[0.05] px-[16px] py-[12px]'
              }
            >
              <ContentLoader loading={balanceIsLoading}>
                <div className={'flex items-center gap-[6px]'}>
                  <p className={'text-[16px] font-semibold leading-[24px] text-aqua-300'}>
                    {t('pages.referralProgram.paid')}
                  </p>
                  <BpTooltip title={t('pages.referralProgram.tooltips.paid')} />
                </div>
                <div className={'text-[24px] font-medium leading-[32px] text-focus-600'}>
                  {setCurrencySymbol(currency, balance?.data?.total_paid ?? 0)}
                </div>
              </ContentLoader>
            </div>
          </div>
          <div>
            <ContentLoader loading={conversionStatIsLoading}>
              {hasStat ? (
                <BpTabs className={'!flex-row pl-[16px]'} size={'md'}>
                  <BpTab title={t('pages.referralProgram.byReferral')}>
                    <ByReferralTable />
                  </BpTab>
                  <BpTab panel={<PeriodSelect />} title={t('pages.referralProgram.byMonth')}>
                    <ByMonthTable />
                  </BpTab>
                  <BpTab title={t('pages.referralProgram.payoutsList')}>
                    <PayoutsList />
                  </BpTab>
                  <BpTab title={t('pages.referralProgram.howItWorks')}>
                    <HowItWorks />
                  </BpTab>
                </BpTabs>
              ) : (
                <div>
                  <p className={'pb-[8px] text-[24px] font-medium text-focus-500'}>
                    {t('pages.referralProgram.howItWorks')}
                  </p>
                  <HowItWorks />
                </div>
              )}
            </ContentLoader>
          </div>
        </div>
        <div
          className={
            'order-1 flex min-w-full max-w-full flex-col gap-[16px] md:min-w-[460px] lg:order-2 lg:min-w-[400px]'
          }
        >
          <div
            className={`flex w-full flex-col gap-[12px] rounded-[12px] bg-focus/[0.05] px-[16px] py-[12px] ${!showConversionRate && !conversionStatIsLoading ? 'hidden' : 'flex'}`}
          >
            <ContentLoader loading={conversionStatIsLoading}>
              <div className={'flex flex-col gap-[4px]'}>
                <p className={'text-[16px] font-semibold leading-[24px] text-aqua-300'}>
                  {t('pages.referralProgram.conversionRate')}
                </p>
                <p className={'text-[13px] leading-[16px] text-aqua-300'}>
                  {t('pages.referralProgram.conversionDescription')}
                </p>
              </div>
              <div className={'pb-[16px] text-[24px] font-medium leading-[32px] text-focus-600'}>
                {conversionStat?.data?.conversion_rate}%
              </div>
              <div className={'flex gap-[8px] py-[2px]'}>
                <div>
                  <p className={'text-[13px] leading-[16px] text-aqua-300'}>
                    {t('pages.referralProgram.clicks')}
                  </p>
                  <div className={'text-[20px] font-medium leading-[28px] text-focus-600'}>
                    {conversionStat ? conversion.clicks : '–'}
                  </div>
                  <div className={'relative h-[56px] w-[50px]'}>
                    <div
                      style={{
                        height: `${statBar.clicks}%`,
                        minHeight: '3%',
                      }}
                      className={'absolute bottom-0 w-full rounded-[4px] bg-[#A8B9CD]'}
                    />
                  </div>
                </div>
                <div>
                  <p className={'text-[13px] leading-[16px] text-aqua-300'}>
                    {t('pages.referralProgram.signups')}
                  </p>
                  <div className={'text-[20px] font-medium leading-[28px] text-focus-600'}>
                    {conversionStat ? conversion.signups : '–'}
                  </div>
                  <div className={'relative h-[56px] w-[50px]'}>
                    <div
                      style={{
                        height: `${statBar.signups}%`,
                        minHeight: '3%',
                      }}
                      className={'absolute bottom-0 w-full rounded-[4px] bg-[#6684A7]'}
                    />
                  </div>
                </div>
                <div>
                  <p className={'text-[13px] leading-[16px] text-aqua-300'}>
                    {t('pages.referralProgram.paidReferrals')}
                  </p>
                  <div className={'text-[20px] font-medium leading-[28px] text-focus-600'}>
                    {conversionStat ? conversion.paid_referrals : '–'}
                  </div>
                  <div className={'relative h-[56px] w-[50px]'}>
                    <div
                      style={{
                        height: `${statBar.paid_referrals}%`,
                        minHeight: '3%',
                      }}
                      className={'absolute bottom-0 w-full rounded-[4px] bg-[#A059FF]'}
                    />
                  </div>
                </div>
              </div>
            </ContentLoader>
          </div>
          <div
            className={
              'flex w-full flex-col gap-[24px] rounded-[12px] bg-focus/[0.05] px-[16px] py-[12px]'
            }
          >
            {(referralCode || linkListIsLoading) && (
              <ContentLoader loading={linkListIsLoading}>
                <div className={'flex flex-col gap-[4px]'}>
                  <p className={'text-[16px] font-semibold leading-[24px] text-aqua-300'}>
                    {t('pages.referralProgram.referralLink')}
                  </p>
                  <div className={'flex rounded-[8px] bg-[#DEE1E7]'}>
                    <div
                      className={'w-full truncate px-[16px] py-[12px] text-[16px] leading-[24px]'}
                    >
                      {t('project.application')}/r/{referralCode}
                    </div>
                    <button
                      className={
                        'min-w-fit rounded-[8px] bg-default px-[16px] text-[14px] font-medium uppercase leading-[24px] text-white'
                      }
                      onClick={handleCopy}
                    >
                      {t('pages.referralProgram.buttons.copyLink')}
                    </button>
                  </div>
                  {
                    {
                      bluepear: (
                        <div>
                          <p className={'text-[13px] text-aqua-300'}>
                            {t('pages.referralProgram.sharingAgree')}{' '}
                            <a
                              className={'text-default underline'}
                              href='https://bluepear.net/referral-program-terms-and-conditions'
                            >
                              {t('pages.referralProgram.referralProgramTC')}
                            </a>
                          </p>
                        </div>
                      ),
                      robuscan: null,
                      default: null,
                    }[process.env.REACT_APP_PROJECT ?? 'default']
                  }
                </div>
              </ContentLoader>
            )}
            <div className={'flex flex-col gap-[4px]'}>
              <p className={'text-[16px] font-semibold leading-[24px] text-aqua-300'}>
                {t('pages.referralProgram.shareInSocials')}
              </p>
              <div className={'flex gap-[8px]'}>
                <button
                  className={
                    'flex h-[48px] w-[48px] items-center justify-center rounded-[8px] border-[1px] border-[#1E1E1E1A]'
                  }
                  onClick={() => handleShare('facebook')}
                >
                  <IconFacebook className={'w-[18px]'} color={'#000000'} />
                </button>
                <button
                  className={
                    'flex h-[48px] w-[48px] items-center justify-center rounded-[8px] border-[1px] border-[#1E1E1E1A]'
                  }
                  onClick={() => handleShare('linkedin')}
                >
                  <IconLinkedin className={'w-[18px]'} color={'#000000'} />
                </button>
                <button
                  className={
                    'flex h-[48px] w-[48px] items-center justify-center rounded-[8px] border-[1px] border-[#1E1E1E1A]'
                  }
                  onClick={() => handleShare('telegram')}
                >
                  <IconTelegram className={'w-[18px]'} color={'#000000'} />
                </button>
                <button
                  className={
                    'flex h-[48px] w-[48px] items-center justify-center rounded-[8px] border-[1px] border-[#1E1E1E1A]'
                  }
                  onClick={() => handleShare('whatsapp')}
                >
                  <IconWhatsapp className={'w-[18px]'} color={'#000000'} />
                </button>
              </div>
            </div>
          </div>
          <div
            className={
              'flex w-full flex-col gap-[24px] rounded-[12px] bg-focus/[0.05] px-[16px] py-[12px]'
            }
          >
            <ContentLoader loading={termsIsLoading}>
              <div className={'flex flex-col gap-[16px]'}>
                <div>
                  <p className={'text-[16px] font-semibold leading-[24px] text-aqua-300'}>
                    {t('pages.referralProgram.payoutDealTitle')}
                  </p>
                  <ul>
                    {!!terms?.data?.revenue_share_percent && (
                      <li className={'text-[20px] font-medium leading-[32px] text-focus-600'}>
                        {t('pages.referralProgram.revenueShareValue', {
                          percent: terms?.data?.revenue_share_percent,
                        })}
                      </li>
                    )}
                    {!!terms?.data?.cpa_reg_amount && (
                      <li className={'text-[20px] font-medium leading-[32px] text-focus-600'}>
                        {t('pages.referralProgram.perSignupValue', {
                          amount: setCurrencySymbol(currency, terms?.data?.cpa_reg_amount),
                        })}
                      </li>
                    )}
                    {!!terms?.data?.cpa_first_payment_amount && (
                      <li className={'text-[20px] font-medium leading-[32px] text-focus-600'}>
                        {t('pages.referralProgram.perFirstPurchaseValue', {
                          amount: setCurrencySymbol(
                            currency,
                            terms?.data?.cpa_first_payment_amount,
                          ),
                        })}
                      </li>
                    )}
                    {!!terms?.data?.full_first_payment_enabled && (
                      <li className={'text-[20px] font-medium leading-[32px] text-focus-600'}>
                        {t('pages.referralProgram.firstPurchaseFullValue', {
                          percent: '100',
                        })}
                      </li>
                    )}
                  </ul>
                </div>
                <div>
                  <p className={'text-[16px] font-semibold leading-[24px] text-aqua-300'}>
                    {t('pages.referralProgram.otherPayoutOptions')}
                  </p>
                  <ul className={'list-disc pl-[16px] text-[16px] leading-[24px] text-focus-500'}>
                    {!terms?.data?.revenue_share_percent && (
                      <li>{t('pages.referralProgram.revenueShareLabel')}</li>
                    )}
                    {!terms?.data?.cpa_reg_amount && (
                      <li>{t('pages.referralProgram.perSignupLabel')}</li>
                    )}
                    {!terms?.data?.cpa_first_payment_amount && (
                      <li>{t('pages.referralProgram.perFirstPurchaseLabel')}</li>
                    )}
                    {!terms?.data?.full_first_payment_enabled && (
                      <li>{t('pages.referralProgram.firstPurchaseFullLabel')}</li>
                    )}
                  </ul>
                </div>
                <div>
                  <BpButton
                    className={'uppercase'}
                    disabled={changeReferralTermsIsFetching || changeReferralTermsIsSuccess}
                    theme={'outline'}
                    onClick={() => {
                      handleContactConfirmed(submitChangeReferralTerms)
                    }}
                  >
                    {terms?.data?.is_default
                      ? t('pages.referralProgram.buttons.requestCustomDeal')
                      : t('pages.referralProgram.buttons.requestDealUpdate')}
                  </BpButton>
                </div>
              </div>
            </ContentLoader>
          </div>
        </div>
      </div>
    </CommonMainPage>
  )
}
